/**
 * ProjectPriorities — Feb 2026 rewrite for Dashboard-parity.
 *
 * Matches the Dashboard "Priorities this week" widget:
 *  • prev / next week toggles (week_offset)
 *  • only the top 3 open items shown up-front (max), rest tucked behind
 *    a "show N more" reveal
 *  • completed items live inside a collapsible accordion at the bottom
 *  • text wraps properly (break-words + whitespace-pre-wrap)
 *  • URLs in titles auto-render as clickable links
 *
 * Data source stays local to the project doc — `project.priorities` — so
 * we don't need a network round-trip on every week flip. The week window
 * uses IST-adjusted Monday→Sunday, mirroring the backend endpoint.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, ListChecks, X, Loader2, AlertTriangle, UserCheck, Clock, Tag as TagIcon,
  Send, CheckCircle2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateFormat";
import linkify from "@/lib/linkify";

const TAG_LIMIT = 10;
const OPEN_LIMIT = 3;  // Feb 2026 · Dashboard-parity — only 3 open shown up-front.

function daysBetween(a, b) {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  const days = Math.max(0, Math.round(ms / 86400000));
  if (days === 0) {
    const hours = Math.max(1, Math.round(ms / 3600000));
    return `${hours}h`;
  }
  return `${days}d`;
}

function delayStatus(p) {
  if (!p.duration_days || p.completed_at) return { delayed: false, daysOver: 0 };
  const elapsedMs = Date.now() - new Date(p.created_at).getTime();
  const elapsedDays = Math.floor(elapsedMs / 86400000);
  return { delayed: elapsedDays > p.duration_days, daysOver: elapsedDays - p.duration_days };
}

/** IST-adjusted Monday for the current week (matches backend). */
function mondayThisWeekIST() {
  const nowUtc = new Date();
  const nowIst = new Date(nowUtc.getTime() + (5.5 * 3600 * 1000));
  const dow = (nowIst.getUTCDay() + 6) % 7;  // Monday = 0
  const monday = new Date(nowIst);
  monday.setUTCDate(nowIst.getUTCDate() - dow);
  monday.setUTCHours(0, 0, 0, 0);
  return monday;
}

/** Return [start, end) of the target week (UTC Date objects). */
function weekWindow(weekOffset) {
  const monday = mondayThisWeekIST();
  monday.setUTCDate(monday.getUTCDate() + weekOffset * 7);
  const end = new Date(monday); end.setUTCDate(monday.getUTCDate() + 7);
  return { start: monday, end, sunday: new Date(end.getTime() - 1) };
}

function inWindow(iso, start, end) {
  if (!iso) return false;
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return false;
  return dt >= start && dt < end;
}

export default function ProjectPriorities({ project, milestones = [], members = [], canEdit = true, onChanged }) {
  const [priorities, setPriorities] = useState(project?.priorities || []);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showAllOpen, setShowAllOpen] = useState(false);
  const [completedOpen, setCompletedOpen] = useState(false);

  const [newTitle, setNewTitle] = useState("");
  const [newMid, setNewMid] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [newDuration, setNewDuration] = useState("");
  const [newTags, setNewTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [defaultTags, setDefaultTags] = useState([]);

  useEffect(() => { setPriorities(project?.priorities || []); }, [project?.priorities]);
  useEffect(() => {
    api.get("/priorities/default-tags").then((r) => setDefaultTags(r.data?.tags || [])).catch(() => {});
  }, []);

  const { start: weekStart, end: weekEnd, sunday } = useMemo(() => weekWindow(weekOffset), [weekOffset]);

  // Week-scoped filter, mirroring backend logic in /priorities/this-week.
  // week_offset == 0 → every OPEN priority (any age).
  // week_offset < 0  → items created OR completed in that ISO week.
  // week_offset > 0  → items with due_date landing in that week.
  const inTargetWeek = useCallback((p) => {
    if (weekOffset === 0) return !p.completed_at;
    if (weekOffset < 0) {
      return inWindow(p.created_at, weekStart, weekEnd) || inWindow(p.completed_at, weekStart, weekEnd);
    }
    const due = p.due_date || p.deadline;
    return inWindow(due, weekStart, weekEnd);
  }, [weekOffset, weekStart, weekEnd]);

  const weekRows = useMemo(() => priorities.filter(inTargetWeek), [priorities, inTargetWeek]);
  const openRows = useMemo(() => weekRows.filter((p) => !p.completed_at), [weekRows]);
  const completedRows = useMemo(() => weekRows.filter((p) => !!p.completed_at), [weekRows]);

  const delayedCount = useMemo(() => openRows.filter((p) => delayStatus(p).delayed).length, [openRows]);
  const openVisible = showAllOpen ? openRows : openRows.slice(0, OPEN_LIMIT);
  const hiddenOpen = openRows.length - openVisible.length;

  const weekLabel = useMemo(() => {
    if (weekOffset === 0) return "This week";
    const fmt = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    return `${fmt(weekStart)} – ${fmt(sunday)}`;
  }, [weekOffset, weekStart, sunday]);

  const headingLabel = weekOffset === 0
    ? "Priorities this week"
    : weekOffset < 0 ? "Priorities last week" : "Priorities next week";

  const addTag = (t) => {
    const clean = (t || "").trim();
    if (!clean || newTags.includes(clean) || newTags.length >= TAG_LIMIT) return;
    setNewTags([...newTags, clean]);
    setNewTagInput("");
  };
  const removeTag = (t) => setNewTags(newTags.filter((x) => x !== t));

  const resetForm = () => {
    setNewTitle(""); setNewMid(""); setNewAssignee("");
    setNewDuration(""); setNewTags([]); setNewTagInput("");
  };

  const add = useCallback(async () => {
    if (!newTitle.trim()) return;
    setBusy(true);
    try {
      const payload = {
        title: newTitle.trim(),
        milestone_id: newMid && newMid !== "__none" ? newMid : null,
        assignee_id: newAssignee && newAssignee !== "__none" ? newAssignee : null,
        duration_days: newDuration ? Math.max(1, parseInt(newDuration, 10) || 0) : null,
        tags: newTags,
      };
      const { data } = await api.post(`/projects/${project.id}/priorities`, payload);
      setPriorities((arr) => [...arr, data]);
      resetForm();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [newTitle, newMid, newAssignee, newDuration, newTags, project?.id, onChanged]);

  const toggle = useCallback(async (p) => {
    try {
      const { data } = await api.patch(`/projects/${project.id}/priorities/${p.id}`, { completed: !p.completed_at });
      setPriorities((arr) => arr.map((x) => (x.id === p.id ? data : x)));
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [project?.id, onChanged]);

  const remove = useCallback(async (p) => {
    if (!window.confirm(`Remove "${p.title}" from priorities?`)) return;
    try {
      await api.delete(`/projects/${project.id}/priorities/${p.id}`);
      setPriorities((arr) => arr.filter((x) => x.id !== p.id));
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [project?.id, onChanged]);

  const reassign = useCallback(async (p, assignee_id) => {
    try {
      const { data } = await api.patch(`/projects/${project.id}/priorities/${p.id}`, { assignee_id });
      setPriorities((arr) => arr.map((x) => (x.id === p.id ? data : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [project?.id]);

  const submitForApproval = useCallback(async (p) => {
    try {
      const { data } = await api.post(`/projects/${project.id}/priorities/${p.id}/submit-for-approval`);
      setPriorities((arr) => arr.map((x) => (x.id === p.id ? data : x)));
      toast.success("Submitted for approval · admin/CM will review on the Approvals page");
    } catch (e) { toast.error(formatApiError(e)); }
  }, [project?.id]);

  const tagSuggestions = useMemo(
    () => defaultTags.filter((t) => !newTags.includes(t)),
    [defaultTags, newTags],
  );

  return (
    <section className="card-flat p-5" data-testid="project-priorities">
      {/* Header + week nav */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
          <ListChecks size={18} className="text-[#F54800]" /> {headingLabel}
        </h2>
        <div className="flex items-center gap-2" data-testid="project-priorities-week-nav">
          <button
            type="button"
            onClick={() => setWeekOffset((n) => n - 1)}
            className="p-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-500 hover:text-slate-800"
            aria-label="Previous week"
            data-testid="project-priorities-prev-week"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            onClick={() => setWeekOffset(0)}
            disabled={weekOffset === 0}
            className={`px-2 py-0.5 text-[11px] rounded-sm border ${weekOffset === 0 ? "border-slate-200 text-slate-400 bg-slate-50 cursor-default" : "border-slate-200 text-slate-700 hover:border-slate-400"}`}
            data-testid="project-priorities-this-week-btn"
          >
            {weekLabel}
          </button>
          <button
            type="button"
            onClick={() => setWeekOffset((n) => n + 1)}
            className="p-1 rounded-sm border border-slate-200 hover:border-slate-400 text-slate-500 hover:text-slate-800"
            aria-label="Next week"
            data-testid="project-priorities-next-week"
          >
            <ChevronRight size={14} />
          </button>
          {delayedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-red-600" data-testid="delayed-count">
              <AlertTriangle size={11} /> {delayedCount} delayed
            </span>
          )}
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold" data-testid="priority-count">
            {openRows.length} open · {completedRows.length} done
          </span>
        </div>
      </div>

      {/* Open priorities (max 3 shown) */}
      <div className="space-y-1.5">
        {openRows.length === 0 && completedRows.length === 0 && (
          <div className="text-xs text-slate-500 italic py-2" data-testid="priorities-empty">
            {weekOffset === 0
              ? "No priorities yet — add focus items for this week."
              : "Nothing landed in this week."}
          </div>
        )}
        {openVisible.map((p) => (
          <PriorityRow
            key={p.id}
            p={p}
            canEdit={canEdit}
            members={members}
            onToggle={toggle}
            onRemove={remove}
            onReassign={reassign}
            onSubmitForApproval={submitForApproval}
          />
        ))}
        {hiddenOpen > 0 && (
          <button
            type="button"
            onClick={() => setShowAllOpen(true)}
            className="w-full text-[11px] text-slate-500 hover:text-[#F54800] py-1.5 border border-dashed border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
            data-testid="project-priorities-show-more"
          >
            Show {hiddenOpen} more open{hiddenOpen === 1 ? "" : ""}
          </button>
        )}
        {showAllOpen && openRows.length > OPEN_LIMIT && (
          <button
            type="button"
            onClick={() => setShowAllOpen(false)}
            className="w-full text-[10px] text-slate-400 hover:text-slate-700 py-1"
            data-testid="project-priorities-show-less"
          >
            Show less
          </button>
        )}
      </div>

      {/* Completed accordion */}
      {completedRows.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3" data-testid="project-priorities-completed-accordion">
          <button
            type="button"
            onClick={() => setCompletedOpen((v) => !v)}
            className="w-full flex items-center justify-between text-xs text-slate-600 hover:text-slate-900 group"
            data-testid="project-priorities-completed-toggle"
            aria-expanded={completedOpen}
          >
            <span className="inline-flex items-center gap-1.5 font-medium">
              {completedOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              <CheckCircle2 size={12} className="text-emerald-600" />
              Completed
              <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm">{completedRows.length}</span>
            </span>
          </button>
          {completedOpen && (
            <div className="space-y-1.5 mt-2">
              {completedRows.map((p) => (
                <PriorityRow
                  key={p.id}
                  p={p}
                  canEdit={canEdit}
                  members={members}
                  onToggle={toggle}
                  onRemove={remove}
                  onReassign={reassign}
                  onSubmitForApproval={submitForApproval}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Composer (only on this-week for clarity) */}
      {canEdit && weekOffset === 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[180px]">
              <Input
                className="rounded-sm h-9"
                placeholder="What's the next priority?"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && add()}
                data-testid="priority-new-title"
              />
            </div>
            <div className="min-w-[160px]">
              <Select value={newMid || "__none"} onValueChange={setNewMid}>
                <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="priority-new-milestone"><SelectValue placeholder="Milestone (opt)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— No milestone —</SelectItem>
                  {milestones.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Select value={newAssignee || "__none"} onValueChange={setNewAssignee}>
                <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="priority-new-assignee"><SelectValue placeholder="Assignee (opt)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Unassigned —</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-24">
              <Input
                type="number"
                min="1"
                className="rounded-sm h-9"
                placeholder="Days"
                value={newDuration}
                onChange={(e) => setNewDuration(e.target.value)}
                data-testid="priority-new-duration"
              />
            </div>
            <Button
              onClick={add}
              disabled={busy || !newTitle.trim()}
              className="rounded-sm h-9 bg-[#F54800] hover:bg-[#D63E00] text-white"
              data-testid="priority-add"
            >
              {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Plus size={13} className="mr-1.5" />}Add
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <TagIcon size={11} className="text-slate-400" />
            {newTags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm bg-[#F54800]/10 text-[#F54800] font-medium">
                {t}
                <button onClick={() => removeTag(t)} className="hover:text-red-600"><X size={9} /></button>
              </span>
            ))}
            {tagSuggestions.slice(0, 10 - newTags.length).map((t) => (
              <button
                key={t}
                onClick={() => addTag(t)}
                className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500 hover:bg-slate-200"
                data-testid={`priority-tag-suggest-${t}`}
              >
                + {t}
              </button>
            ))}
            {newTags.length < TAG_LIMIT && (
              <Input
                className="rounded-sm h-7 text-[11px] w-32"
                placeholder="Custom tag…"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(newTagInput); } }}
                data-testid="priority-tag-input"
              />
            )}
            {newTags.length >= TAG_LIMIT && (
              <span className="text-[10px] text-slate-400 italic">Max {TAG_LIMIT} tags</span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Single priority row                                                        */
/* -------------------------------------------------------------------------- */
function PriorityRow({ p, canEdit, members, onToggle, onRemove, onReassign, onSubmitForApproval }) {
  const done = !!p.completed_at;
  const duration = done ? daysBetween(p.created_at, p.completed_at) : null;
  const { delayed, daysOver } = delayStatus(p);
  return (
    <div
      className={`flex items-start gap-3 px-3 py-2 rounded-sm border transition-colors ${
        done ? "bg-slate-50 border-slate-200" :
        delayed ? "border-red-300 bg-red-50/40" :
        "border-slate-200 hover:bg-slate-50"
      }`}
      data-testid={`priority-${p.id}`}
    >
      <input
        type="checkbox"
        checked={done}
        disabled={!canEdit}
        onChange={() => onToggle(p)}
        className="w-4 h-4 accent-[#F54800] cursor-pointer shrink-0 mt-1"
        data-testid={`priority-check-${p.id}`}
      />
      <div className="flex-1 min-w-0">
        <div
          className={`text-sm break-words whitespace-pre-wrap ${done ? "line-through text-slate-400" : delayed ? "text-red-700 font-medium" : "text-slate-900"}`}
          data-testid={`priority-title-${p.id}`}
        >
          {linkify(p.title)}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {p.milestone_title && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#F54800]/10 text-[#F54800] font-semibold">{p.milestone_title}</span>
          )}
          {p.assignee_name && (
            <span className="text-[10px] inline-flex items-center gap-1 text-slate-600" data-testid={`priority-assignee-${p.id}`}>
              <UserCheck size={10} /> {p.assignee_name}
            </span>
          )}
          {p.duration_days && !done && (
            <span className={`text-[10px] inline-flex items-center gap-1 ${delayed ? "text-red-700 font-bold" : "text-slate-500"}`} data-testid={`priority-duration-${p.id}`}>
              <Clock size={10} /> {p.duration_days}d target{delayed ? ` · ${daysOver}d over` : ""}
            </span>
          )}
          <span className="text-[10px] text-slate-400">
            {done
              ? <>Done {formatDate(p.completed_at)} · started {formatDate(p.created_at)}</>
              : <>Started {formatDate(p.created_at)}</>}
          </span>
          {(p.tags || []).map((t) => (
            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">
              #{t}
            </span>
          ))}
          {p.approval_status === "submitted" && (
            <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700 font-medium" data-testid={`priority-approval-status-${p.id}`}>
              <Send size={9} /> Awaiting approval
            </span>
          )}
          {p.approval_status === "approved" && (
            <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 font-medium" data-testid={`priority-approval-status-${p.id}`}>
              <CheckCircle2 size={9} /> Approved{p.reviewed_by_name ? ` · ${p.reviewed_by_name}` : ""}
            </span>
          )}
          {p.approval_status === "rejected" && (
            <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 font-medium" data-testid={`priority-approval-status-${p.id}`}>
              <X size={9} /> Sent back · please revise
            </span>
          )}
        </div>
        {p.approval_status === "rejected" && p.review_note && (
          <div className="mt-1 text-[11px] bg-red-50 border border-red-200 text-red-800 rounded-sm px-2 py-1 break-words whitespace-pre-wrap" data-testid={`priority-review-note-${p.id}`}>
            <span className="font-semibold">Reviewer:</span> {linkify(p.review_note)}
          </div>
        )}
      </div>
      {done && (
        <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 shrink-0 mt-1">{duration}</span>
      )}
      {canEdit && !done && (p.approval_status === "draft" || !p.approval_status || p.approval_status === "rejected") && (
        <button
          onClick={() => onSubmitForApproval(p)}
          className="text-[10px] px-2 py-1 rounded-sm border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 inline-flex items-center gap-1 shrink-0"
          title="Send this priority for admin/CM approval"
          data-testid={`priority-submit-${p.id}`}
        >
          <Send size={10} /> Submit
        </button>
      )}
      {canEdit && !done && members.length > 0 && (
        <Select value={p.assignee_id || "__none"} onValueChange={(v) => onReassign(p, v === "__none" ? "" : v)}>
          <SelectTrigger className="h-7 text-[10px] rounded-sm w-32 shrink-0" data-testid={`priority-assign-${p.id}`}>
            <SelectValue placeholder="Assign…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">— Unassigned —</SelectItem>
            {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {canEdit && (
        <button onClick={() => onRemove(p)} className="text-slate-300 hover:text-red-600 shrink-0 mt-1" data-testid={`priority-delete-${p.id}`}>
          <X size={13} />
        </button>
      )}
    </div>
  );
}
